<template>
  <div class="bc4 pt10 pb10 md-6 lg-6 max5 margin-center">

    <!-- switch locale -->
    <div class="flex xs-center">
      <select v-on:change="switchLocale">
        <option v-for="(locale, i) in locales" :key="`locale${i}`" :value="locale.value">{{ locale.name }}</option>
      </select>
    </div>
    <!-- logo -->
    <div class="flex xs-center">
      <div class="flex xs-10 xs-left">
        <div class="mr1" style="height: 80px;">
          <img :src="require('@/assets/img/logos/mgg.png')" alt="mgg-logo" class="img-responsive" style="height: auto; max-height: 100%" />
        </div>
      </div>
    </div>

    <div v-if="!processed">
      <!-- form -->
      <div class="flex wrap xs-center pt10">
        <div class="flex xs-10 xs-right">
          <!-- secure right align -->
          <div class="flex xs-middle">
            <img :src="require('@/assets/img/lock.svg')" alt="secure" height="18" />
            <j-base-label class="fs3 fw7 cSuccess ml1">{{ $t("checkout.secure-payment") }}</j-base-label>
          </div>
        </div>
        
        <!-- inner form container -->
        <div class="xs-10">        
          <!-- row - name -->
          <div class="row mb4">
            <j-base-label for="name" class="lh150">{{ $t("checkout.name") }}</j-base-label>
            <j-base-input-text 
              id="name"
              name="name"
              v-validate="{ required: true, alpha_spaces: true }"
              :placeholder="$t(`checkout.name-placeholder`)"
              v-model="payload.name"
              autocomplete="name"
            ></j-base-input-text>
            <j-text v-show="errors.has('name')" class="cError lh125 fs3">{{ errors.first('name') }}</j-text>
          </div>

          <!-- row - email -->
          <div class="row mb4">
            <j-base-label for="email" class="lh150">{{ $t("checkout.email") }}</j-base-label>
            <j-base-input-text 
              id="email"
              name="email"
              v-validate="{ required: true, email: true}"
              :placeholder="$t(`checkout.email-placeholder`)"
              v-model="payload.email"
            ></j-base-input-text>
            <j-text v-show="errors.has('email')" class="cError lh125 fs3">{{ errors.first('email') }}</j-text>
          </div>
          
          <!-- row - policy -->
          <div class="row mb4">
            <j-base-label for="policy" class="lh150">{{ $t("checkout.policy") }}</j-base-label>
            <j-base-input-text 
              id="policy"
              name="policy"
              :placeholder="$t(`checkout.policy-placeholder`)"
              v-model="payload.policy"
            ></j-base-input-text>
            <j-text v-show="errors.has('policy')" class="cError lh125 fs3">{{ errors.first('policy') }}</j-text>
          </div>

          <!-- row - payment amount -->
          <div class="row mb4">
            <j-base-label for="amount" class="lh150">{{ $t("checkout.payment-amount") }}</j-base-label>
            <j-base-input-number 
              id="amount"
              name="amount"    
              :placeholder="$t(`checkout.payment-amount-placeholder`)"
              v-validate="{ required: true, decimal: 2, min_value: '0.10' }"
              v-model="payload.amount"
              @input="calculateTotal(payload.amount)"
            ></j-base-input-number>
            <j-text v-show="errors.has('amount')" class="cError fs3 lh125">{{ errors.first('amount') }}</j-text>
          </div>

          <!-- row - credit card input -->
          <div class="row mb4">
            <j-base-label for="cc" class="lh150">{{ $t("checkout.credit-card") }}</j-base-label>
            <div class="StripeElement" ref="card"></div>
            <div class="cError mt2 fs3 lh125" v-if="errorMessage">{{errorMessage}}</div>
          </div>
        </div>

        <div class="xs-10 mt4">
          <!-- total -->
          <div class="mb6 xs-right">
            <div class="flex xs-right">
              <h3 class="fs6 lh125">{{ $t("checkout.total") }}: {{ total ? `${total} HKD` : '0.00 HKD' }}</h3>
            </div>
            <div class="flex xs-right">
              <span class="fs3">{{ $t("checkout.total-includes") }}</span>
            </div>
          </div>
          
          <div class="flex xs-center">
            <!-- buy button -->
            <j-button 
              :class="[{ normal: isValid }, { loading: loading }, { disabled: !isValid }]"
              @click.native="onSubmit">
              <span>{{ !loading ? $t("checkout.proceed")  : $t("checkout.loading") }}</span>
            </j-button>
          </div>
        </div>
      </div>
      
      <!-- payment logos -->
      <payment-icons></payment-icons>
    </div>

    <div class="flex xs-center" v-if="processed">
      <thank-you></thank-you>
    </div>
  </div>
</template>

<script>
import "@/assets/css/index.css";
import { Validator } from "vee-validate";

import PaymentIcons from "./PaymentIcons";
import ThankYou from "./ThankYou";

import JBaseInputNumber from "./JBaseInputNumber";
import JBaseInputText from "./JBaseInputText";
import JBaseLabel from "./JBaseLabel";
import JButton from "./JButton";
import JText from "./JText";

import Config from "../config";
import { getUrlParameters } from "@/lib/utils";

import gql from "@/lib/api";
import { mutation, query } from "@/lib/query";

const stripe = Stripe(process.env.STRIPE_API_KEY); // update api key to live for production
let card = null;
let parameters = null;

export default {
  name: "CheckoutView",
  components: {
    JBaseInputNumber,
    JBaseInputText,
    JBaseLabel,
    JButton,
    JText,

    PaymentIcons,
    ThankYou
  },
  props: {
    initialPayload: {
      type: Object,
      required: false,
      default: () => ({
        name: "",
        email: "",
        policy: "",
        amount: null
      })
    },
    tenantId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      payload: { ...this.initialPayload },
      total: null,
      loading: false,
      processed: false,
      errorMessage: null,
      isValid: false,
      locales: Config.locales,
      tenantToken: ""
    };
  },
  methods: {
    calculateTotal(amount) {
      const paymentFee = 5 / 100;
      this.total = parseFloat(
        Math.round(amount * (1 + paymentFee) * 10) / 10
      ).toFixed(2);
    },

    async onSubmit() {
      this.loading = true;

      const { amount, policy, name, email } = this.payload;
      const total = this.total;
      // validate
      let { token, error } = await stripe.createToken(card);

      // show error
      if (error) {
        this.loading = false;
        this.errorMessage = error.message;
        return;
      }

      let paymentToken = this.tenantToken;

      // pay -> gql endpoint
      try {
        // create payment with token

        const variables = {
          input: {
            description: `${name}, ${email}`,
            policyId: policy,
            amount: total, // includes fee percentage
            currencyCode: "HKD"
          }
        };

        const transactionId = await gql({
          headers: { Authorization: `Bearer ${paymentToken}` },
          query: mutation.initialize,
          variables: variables
        }).then(res => {
          return res.data.initializeTransaction.value.transactionId;
        });

        const paymentData = await gql({
          headers: { Authorization: `Bearer ${paymentToken}` },
          query: mutation.process,
          variables: {
            transactionId: transactionId,
            input: {
              providerToken: token.id
            }
          }
        }).then(res => {
          return res.data.processTransaction;
        });

        this.loading = false;

        if (paymentData.status !== "success") {
          const errorMessage = paymentData.errors.reduce(
            (acc, cur) => (acc += " " + cur),
            ""
          );
          this.errorMessage = errorMessage;
          return;
        }

        this.processed = true;
      } catch (e) {
        this.errorMessage = "There was an error with your payment request";
        this.loading = false;
        this.processed = false;
      }
    },

    switchLocale(event) {
      const locale = event.target.value;

      this.$i18n.locale = locale;
      Validator.localize(locale);
      this.createCardInput(locale);
    },
    createCardInput(locale) {
      let elements = stripe.elements({
        locale: locale
      });
      const style = {
        base: {
          fontSize: "1rem",
          color: "#111319",
          fontFamily: `"Roboto", sans-serif`
        }
      };
      // create an instance of the card Element
      card = elements.create("card", { hidePostalCode: true, style });
      // Add an instance of the card Element to the `card` <div>
      card.mount(this.$refs.card);
    }
  },
  mounted() {
    this.createCardInput();

    const dictionary = {
      mgg: {
        tenantId: "mgg",
        username: "paymentGateway",
        password: "paymentGateway",
        clientId: "paymentGateway"
      },
      mgg_uat: {
        tenantId: "mgg_uat",
        username: "paymentGateway",
        password: "paymentGateway",
        clientId: "paymentGateway"
      }
    };

    const credentials = dictionary[this.tenantId];

    const getToken = gql({
      query: query.tokenQuery,
      variables: credentials
    }).then(res => {
      this.tenantToken = res.data.token_2.accessToken;
    });
  },
  watch: {
    payload: {
      async handler() {
        this.errorMessage = ""; // remove previous errorMessage (perhaps user updates form)
        const isFormEmpty = Object.values(this.payload).some(
          value => value === "" || value === null
        );
        const isFormValid = async () =>
          new Promise(resolve => {
            this.$validator.validateAll().then(isValid => resolve(isValid));
          });

        this.isValid = isFormEmpty ? false : await isFormValid();
      },
      deep: true
    }
  }
};
</script>


<style scoped>
@import "../assets/css/tokens.css";
.StripeElement {
  width: 100%;
  background-color: var(--color5);
  border: 1px solid var(--color7);
  border-radius: var(--borderRadius1);
  height: 40px;
  padding: 10px 12px;
}

.row {
  min-height: 72px;
}
</style>



// WEBPACK FOOTER //
// src/components/CheckoutView.vue